import { customFetch } from "@workspace/api-client-react";

const TRACKING_KEYS = {
  CLICK_ID: "vnz_click_id",
  CLICK_ID_TYPE: "vnz_click_id_type",
  UTM_SOURCE: "vnz_utm_source",
  UTM_MEDIUM: "vnz_utm_medium",
  KEYWORD: "vnz_keyword",
  UTM_CAMPAIGN_ID: "vnz_utm_campaign_id",
  UTM_CAMPAIGN: "vnz_utm_campaign",
  UTM_ADGROUP_ID: "vnz_utm_adgroup_id",
  UTM_ADGROUP: "vnz_utm_adgroup",
  UTM_AD_ID: "vnz_utm_ad_id",
  UTM_AD: "vnz_utm_ad",
  ADS_WHATSAPP: "vnz_ads_whatsapp",
  ADS_WHATSAPP_JID: "vnz_ads_whatsapp_jid",
  ADS_FIRST_NAME: "vnz_ads_first_name",
  RESERVED_USER_ID: "vnz_reserved_user_id",
  LEAD_CONVERSION_ID: "vnz_lead_conversion_id",
  GEO_COUNTRY: "vnz_geo_country",
  GEO_STATE: "vnz_geo_state",
  GEO_CITY: "vnz_geo_city",
  GEO_ZIP: "vnz_geo_zip",
  GEO_FETCHED: "vnz_geo_fetched",
  LEAD_SENT: "vnz_lead_sent",
  LANDING_URL: "vnz_landing_url",
};

// localStorage pode lançar SecurityError em navegador in-app (WebView do
// WhatsApp) com storage bloqueado. Tracking é best-effort e NUNCA pode
// derrubar registro/checkout — incidente 03/07/2026: getTrackingData num
// WebView sem a chave gravada mandava reservedUserId null e o Zod do
// /auth/register barrava o cadastro do lojista.
function lsGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function lsSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // storage bloqueado — segue sem persistir (tracking degrada, app não)
  }
}

function getFbpCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)_fbp=([^;]*)/);
  return match ? match[1] : null;
}

function getApiBase(): string {
  // Em prod com subdomínio split (api.vendanozap.app), VITE_API_URL é injetado
  // no build. Em dev (proxy Vite -> localhost:8080), chamadas relativas batem
  // no proxy via BASE_URL.
  const fromEnv = (import.meta.env.VITE_API_URL as string | undefined)?.trim();
  if (fromEnv) return fromEnv.replace(/\/+$/, "");
  return (import.meta.env.BASE_URL || "/").replace(/\/+$/, "");
}

async function fetchGeoData() {
  if (lsGet(TRACKING_KEYS.GEO_FETCHED)) return;

  try {
    const resp = await fetch(`${getApiBase()}/api/conversions/geo`, {
      method: "GET",
      credentials: "include",
    });
    if (!resp.ok) return;
    const data = await resp.json();
    if (data.country) lsSet(TRACKING_KEYS.GEO_COUNTRY, data.country);
    if (data.region) lsSet(TRACKING_KEYS.GEO_STATE, data.region);
    if (data.city) lsSet(TRACKING_KEYS.GEO_CITY, data.city);
    if (data.postal) lsSet(TRACKING_KEYS.GEO_ZIP, data.postal);
    lsSet(TRACKING_KEYS.GEO_FETCHED, "1");
  } catch {
  }
}

async function fireLeadEvent() {
  if (lsGet(TRACKING_KEYS.LEAD_SENT)) return;

  const clickId = lsGet(TRACKING_KEYS.CLICK_ID);

  let reservedUserId = lsGet(TRACKING_KEYS.RESERVED_USER_ID);
  if (!reservedUserId) {
    reservedUserId = crypto.randomUUID();
    lsSet(TRACKING_KEYS.RESERVED_USER_ID, reservedUserId);
  }
  const landingUrl = lsGet(TRACKING_KEYS.LANDING_URL) || window.location.href;

  try {
    const resp = await fetch(`${getApiBase()}/api/conversions/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        event: "Lead",
        reservedUserId,
        clickId,
        clickIdType: lsGet(TRACKING_KEYS.CLICK_ID_TYPE),
        firstName: lsGet(TRACKING_KEYS.ADS_FIRST_NAME),
        phone: lsGet(TRACKING_KEYS.ADS_WHATSAPP),
        phoneJid: lsGet(TRACKING_KEYS.ADS_WHATSAPP_JID),
        utmSource: lsGet(TRACKING_KEYS.UTM_SOURCE),
        utmMedium: lsGet(TRACKING_KEYS.UTM_MEDIUM),
        keyword: lsGet(TRACKING_KEYS.KEYWORD),
        utmCampaignId: lsGet(TRACKING_KEYS.UTM_CAMPAIGN_ID),
        utmCampaign: lsGet(TRACKING_KEYS.UTM_CAMPAIGN),
        utmAdgroupId: lsGet(TRACKING_KEYS.UTM_ADGROUP_ID),
        utmAdgroup: lsGet(TRACKING_KEYS.UTM_ADGROUP),
        utmAdId: lsGet(TRACKING_KEYS.UTM_AD_ID),
        utmAd: lsGet(TRACKING_KEYS.UTM_AD),
        fbp: getFbpCookie(),
        country: lsGet(TRACKING_KEYS.GEO_COUNTRY),
        state: lsGet(TRACKING_KEYS.GEO_STATE),
        city: lsGet(TRACKING_KEYS.GEO_CITY),
        zip: lsGet(TRACKING_KEYS.GEO_ZIP),
        eventSourceUrl: landingUrl,
      }),
    });

    if (resp.ok) {
      const result = await resp.json();
      if (result?.id) {
        lsSet(TRACKING_KEYS.LEAD_CONVERSION_ID, result.id);
        lsSet(TRACKING_KEYS.LEAD_SENT, "1");
      }
    }
  } catch {
  }
}

fetchGeoData();

export { fireLeadEvent };

export function getTrackingData() {
  return {
    reservedUserId: lsGet(TRACKING_KEYS.RESERVED_USER_ID),
    leadConversionId: lsGet(TRACKING_KEYS.LEAD_CONVERSION_ID),
    adsWhatsapp: lsGet(TRACKING_KEYS.ADS_WHATSAPP),
    adsWhatsappJid: lsGet(TRACKING_KEYS.ADS_WHATSAPP_JID),
    adsFirstName: lsGet(TRACKING_KEYS.ADS_FIRST_NAME),
    clickId: lsGet(TRACKING_KEYS.CLICK_ID),
    clickIdType: lsGet(TRACKING_KEYS.CLICK_ID_TYPE),
    utmSource: lsGet(TRACKING_KEYS.UTM_SOURCE),
    utmMedium: lsGet(TRACKING_KEYS.UTM_MEDIUM),
    keyword: lsGet(TRACKING_KEYS.KEYWORD),
    utmCampaignId: lsGet(TRACKING_KEYS.UTM_CAMPAIGN_ID),
    utmCampaign: lsGet(TRACKING_KEYS.UTM_CAMPAIGN),
    utmAdgroupId: lsGet(TRACKING_KEYS.UTM_ADGROUP_ID),
    utmAdgroup: lsGet(TRACKING_KEYS.UTM_ADGROUP),
    utmAdId: lsGet(TRACKING_KEYS.UTM_AD_ID),
    utmAd: lsGet(TRACKING_KEYS.UTM_AD),
    country: lsGet(TRACKING_KEYS.GEO_COUNTRY),
    state: lsGet(TRACKING_KEYS.GEO_STATE),
    city: lsGet(TRACKING_KEYS.GEO_CITY),
    zip: lsGet(TRACKING_KEYS.GEO_ZIP),
  };
}

export async function trackEvent(
  event: "CompleteRegistration" | "InitiateCheckout",
  userId?: string,
  value?: number,
) {
  const data = getTrackingData();
  try {
    await customFetch("/api/conversions/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event,
        userId,
        value: event === "InitiateCheckout" ? value : undefined,
        reservedUserId: data.reservedUserId,
        leadConversionId: data.leadConversionId,
        clickId: data.clickId,
        clickIdType: data.clickIdType,
        utmSource: data.utmSource,
        utmMedium: data.utmMedium,
        keyword: data.keyword,
        utmCampaignId: data.utmCampaignId,
        utmCampaign: data.utmCampaign,
        utmAdgroupId: data.utmAdgroupId,
        utmAdgroup: data.utmAdgroup,
        utmAdId: data.utmAdId,
        utmAd: data.utmAd,
        fbp: getFbpCookie(),
        country: data.country,
        state: data.state,
        city: data.city,
        zip: data.zip,
        eventSourceUrl: window.location.href,
      }),
    });
  } catch {
  }
}

export async function trackCompleteRegistration(userId: string) {
  const leadConversionId = lsGet(TRACKING_KEYS.LEAD_CONVERSION_ID);
  if (leadConversionId) {
    try {
      await customFetch("/api/conversions/update-lead-user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversionId: leadConversionId }),
      });
    } catch {
    }
  }

  await trackEvent("CompleteRegistration", userId);
}
